import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Heading, Box, Text, Button, SimpleGrid, Image, Table, Thead, Tr } from '@chakra-ui/react';
import { supabase } from '../supabaseClient';
import { RaceStats } from '../types/types';
import { timeToSeconds } from '../utils/timeUtils';
import { TableHeader } from './shared/TableHeader';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import BackButton from './shared/BackButton';
// Import race configuration data
import raceConfig from '../config/races.json';

// Register the new chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement  // Add this for pie charts
);

// Add SortConfig interface
interface SortConfig {
  key: keyof RaceStats | null;
  direction: 'asc' | 'desc' | null;
}

interface RaceData {
  race_code: string;
  race_description: string;
  race_date: string;
  race_location: string;
  race_propositions: string;
  race_discipline: string;
}

// Add date formatting function at the top of the component
const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

// Add normalizeTime helper function
const normalizeTime = (timeStr: string): number => {
  if (!timeStr) return 999999;
  
  // Handle MM:SS.ms format (1:00.20)
  if (timeStr.includes(':') && timeStr.split(':').length === 2) {
    const [minutes, secondsPart] = timeStr.split(':');
    return (parseInt(minutes) * 60) + parseFloat(secondsPart);
  }
  
  // Handle HH:MM:SS.ms format (0:00:34.28)
  if (timeStr.includes(':') && timeStr.split(':').length === 3) {
    const [hours, minutes, secondsPart] = timeStr.split(':');
    return (parseInt(hours) * 3600) + (parseInt(minutes) * 60) + parseFloat(secondsPart);
  }
  
  // Handle SS.ms format (50.95)
  return parseFloat(timeStr);
};

// Update the isValidUrl function to handle undefined values
const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

// Add this helper function for generating Chakra UI styled colors
const generateChartColors = (count: number) => {
  const colors = [
    '#4299E1', // blue.400
    '#48BB78', // green.400
    '#ED8936', // orange.400
    '#9F7AEA', // purple.400
    '#F687B3', // pink.400
    '#63B3ED', // blue.300
    '#68D391', // green.300
    '#F6AD55', // orange.300
    '#B794F4', // purple.300
    '#F8BBD0'  // pink.300
  ];
  return Array(count).fill(0).map((_, i) => colors[i % colors.length]);
};

export default function RaceDetail() {
  const { race_code } = useParams();
  const navigate = useNavigate();
  const [raceData, setRaceData] = useState<RaceData | null>(null);
  const [racersByCategory, setRacersByCategory] = useState<Record<string, RaceStats[]>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: null });
  // Get race-specific config if available
  const raceSpecificConfig = race_code ? (raceConfig as Record<string, any>)[race_code] || null : null;

  useEffect(() => {
    const fetchRaceData = async () => {
      // Ensure we have a valid race_code before querying
      if (!race_code) {
        console.error('Missing race_code for RaceDetail query');
        return;
      }

      // Fetch race details
      const { data: raceDetails, error: raceError } = await supabase
        .from('medvedica_race_data')
        .select('*')
        .eq('race_code', race_code)
        .single();

      if (raceError) {
        console.error('Error fetching race data:', raceError);
        return;
      }

      setRaceData(raceDetails);

      // Fetch racers in this race
      const { data: racersData, error: racersError } = await supabase
        .from('medvedica_race_stats')
        .select('*')
        .eq('race_code', race_code);

      if (racersError) {
        console.error('Error fetching racers:', racersError);
        return;
      }

      // Group racers by category
      const typedRacers = (racersData || []) as RaceStats[];
      const grouped = typedRacers.reduce((acc, racer) => {
        const category = racer.race_category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(racer);
        return acc;
      }, {} as Record<string, RaceStats[]>);

      // Sort racers by position within each category
      const sortedGrouped = Object.keys(grouped).sort().reduce((acc, key) => {
        acc[key] = grouped[key].sort((a: RaceStats, b: RaceStats) => {
          const posA = a.position;
          const posB = b.position;

          // Handle null/undefined values
          if (!posA && !posB) return 0;
          if (!posA) return 1; // null/undefined goes last
          if (!posB) return -1;

          // Convert to numbers, handling special cases
          const numA = posA === "0" ? Infinity : parseInt(posA);
          const numB = posB === "0" ? Infinity : parseInt(posB);

          // Handle NaN cases (strings)
          if (isNaN(numA) && isNaN(numB)) return posA.localeCompare(posB);
          if (isNaN(numA)) return 1; // strings go last
          if (isNaN(numB)) return -1;

          // Normal numeric comparison
          return numA - numB;
        });
        return acc;
      }, {} as Record<string, RaceStats[]>);

      setRacersByCategory(sortedGrouped);
    };

    fetchRaceData();
  }, [race_code]);

  // Add sorting functions
  const handleSort = (key: keyof RaceStats) => {
    setSortConfig(current => {
      if (current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (current.direction === 'desc') {
        return { key: null, direction: null };
      }
      return { key, direction: 'asc' };
    });
  };

  // Update sortData function
  const sortData = (data: RaceStats[]): RaceStats[] => {
    if (!sortConfig.key || !sortConfig.direction) return data;
    const key = sortConfig.key;

    return [...data].sort((a, b) => {
      // Handle null values
      const aValue = a[key];
      const bValue = b[key];
      
      if (aValue === null || aValue === undefined) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return sortConfig.direction === 'desc' ? 1 : -1;
      }

      // Special handling for time-based columns
      if (['total_time', 'first_run', 'second_run'].includes(key)) {
        const aTime = normalizeTime(aValue.toString());
        const bTime = normalizeTime(bValue.toString());
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      // Handle position as number
      if (key === 'position') {
        if (!aValue && !bValue) return 0;
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        
        const aPos = parseInt(aValue.toString());
        const bPos = parseInt(bValue.toString());
        return sortConfig.direction === 'asc' ? aPos - bPos : bPos - aPos;
      }

      // Default string comparison
      const aStr = aValue.toString().toLowerCase();
      const bStr = bValue.toString().toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  };

  // Update ResultsTable component – size="sm" on mobile for compact rows
  const ResultsTable = ({ racers }: { racers: RaceStats[] }) => (
    <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
      <Thead bg="gray.50">
        <Tr>
          <TableHeader label="Position" sortKey="position" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="Racer Name" sortKey="name" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="Year" sortKey="year" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="Team" sortKey="team" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="Total Time" sortKey="total_time" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="First Run" sortKey="first_run" sortConfig={sortConfig} onSort={handleSort} />
          <TableHeader label="Second Run" sortKey="second_run" sortConfig={sortConfig} onSort={handleSort} />
        </Tr>
      </Thead>
      <tbody>
        {sortData(racers).map((racer) => (
          <tr key={racer.id}>
            <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
              {racer.position || 'DNF'}
            </td>
            <td 
              style={{ 
                padding: '8px 10px', 
                borderBottom: '1px solid #E2E8F0', 
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/racer/${encodeURIComponent(racer.name)}`)}
              onMouseEnter={(e) => e.currentTarget.style.color = 'darkblue'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
            >
              {racer.name}
            </td>
            <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>
              {racer.year || '-'}
            </td>
            {/* Team cell – truncated with ellipsis to prevent multi-line row inflation */}
            <td 
              style={{ 
                padding: '8px 10px', 
                borderBottom: '1px solid #E2E8F0', 
                cursor: racer.team ? 'pointer' : 'default',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={racer.team || ''}
              onClick={() => racer.team && navigate(`/teamDetail/${encodeURIComponent(racer.team)}`)}
              onMouseEnter={(e) => racer.team && (e.currentTarget.style.color = 'darkblue')}
              onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
            >
              {racer.team || '-'}
            </td>
            <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.total_time}</td>
            <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.first_run}</td>
            <td style={{ padding: '8px 10px', borderBottom: '1px solid #E2E8F0' }}>{racer.second_run}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  // Add new function to get unique categories with counts
  const getCategorySummary = () => {
    return Object.entries(racersByCategory).map(([category, racers]) => ({
      category,
      count: racers.length
    }));
  };

  // First add this helper function to get team participation data
  const getTeamParticipation = () => {
    const teamCounts: Record<string, number> = {};
    
    Object.values(racersByCategory).flat().forEach(racer => {
      if (racer.team) {
        teamCounts[racer.team] = (teamCounts[racer.team] || 0) + 1;
      }
    });

    // Sort teams by participation count (descending)
    return Object.entries(teamCounts)
      .sort(([, a], [, b]) => b - a)
      .reduce((acc, [team, count]) => ({
        teams: [...(acc.teams || []), team],
        counts: [...(acc.counts || []), count]
      }), { teams: [], counts: [] } as { teams: string[], counts: number[] });
  };

  return (
    <Container maxW="container.xl" py={8}>
      <BackButton to="/races" label="Back to races" />
      
      <Box px={4} pt={2}>
        <Heading mb={8}>Race Results</Heading>

        {/* Race Information Box */}
        <Box p={5} shadow="md" borderWidth="1px" borderRadius="md" mb={8}>
          <SimpleGrid columns={[1, null, 2]} spacing={6}>
            <Box>
              <Heading size="md" mb={4}>{raceData?.race_description || 'Loading...'}</Heading>
              <Text mb={2}><strong>Date:</strong> {raceData?.race_date ? formatDate(raceData.race_date) : '-'}</Text>
              <Text mb={2}><strong>Location:</strong> {raceData?.race_location || '-'}</Text>
              <Text mb={2}><strong>Total Racers:</strong> {Object.values(racersByCategory).flat().length}</Text>
              <Text mb={2}><strong>Discipline:</strong> {raceData?.race_discipline || '-'}</Text>
              <Text mb={2}>
                <strong>Propositions:</strong>{' '}
                {raceData && isValidUrl(raceData.race_propositions) ? (
                  <Text
                    as="a"
                    href={raceData.race_propositions}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="blue.500"
                    textDecoration="underline"
                    display="inline"
                    cursor="pointer"
                    /* Allow long URLs to break at any character on mobile */
                    wordBreak="break-all"
                    maxWidth={{ base: '100%', md: '500px' }}
                  >
                    {raceData.race_propositions}
                  </Text>
                ) : (
                  raceData?.race_propositions || '-'
                )}
              </Text>
              
              {/* Display annotation if available */}
              {raceSpecificConfig?.annotation && (
                <Text mt={4} fontStyle="italic" color="gray.600">
                  {raceSpecificConfig.annotation}
                </Text>
              )}
            </Box>
            <Box>
              <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center"
                height="200px"
                width="100%"
                overflow="hidden"
                borderRadius="md"
                bg="gray.50"
                position="relative"
              >
                <Image
                  src={raceSpecificConfig?.logo || "/img/race-results.webp"}
                  alt={raceData?.race_description || "Race results"}
                  position="absolute"
                  top="0"
                  left="0"
                  width="100%"
                  height="100%"
                  objectFit={raceSpecificConfig?.logo ? "contain" : "cover"}
                  objectPosition="center"
                  fallback={<Box bg="gray.200" h="200px" w="100%" borderRadius="md" />}
                />
              </Box>
            </Box>
          </SimpleGrid>
        </Box>

        <SimpleGrid columns={[1, null, 2]} spacing={6} mb={8}>
          {/* Race details box */}
          {raceData && (
            <Box p={5} shadow="md" borderWidth="1px" borderRadius="md">
              <Heading size="md" mb={4}>Race & Category stats</Heading>
              <Box height="300px" position="relative" mb={8}>
                <Pie
                  data={{
                    labels: getCategorySummary().map(item => item.category),
                    datasets: [{
                      data: getCategorySummary().map(item => item.count),
                      backgroundColor: generateChartColors(getCategorySummary().length),
                      borderColor: '#E2E8F0', // gray.200
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right' as const,
                        labels: {
                          boxWidth: 20,
                          padding: 15,
                          color: '#4A5568' // gray.600
                        }
                      },
                      title: {
                        display: true,
                        text: 'Category Distribution',
                        color: '#2D3748', // gray.700
                        font: {
                          size: 16
                        }
                      }
                    }
                  }}
                />
              </Box>
              
              {/* Add Team Participation Bar Chart */}
              <Box height="400px" position="relative">
                <Bar
                  data={{
                    labels: getTeamParticipation().teams,
                    datasets: [{
                      label: 'Number of Racers',
                      data: getTeamParticipation().counts,
                      backgroundColor: '#4299E1', // blue.400
                      borderColor: '#2B6CB0', // blue.600
                      borderWidth: 1
                    }]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y' as const,
                    plugins: {
                      legend: {
                        display: false
                      },
                      title: {
                        display: true,
                        text: 'Team Participation',
                        color: '#2D3748', // gray.700
                        font: {
                          size: 16
                        }
                      }
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Number of Racers',
                          color: '#4A5568' // gray.600
                        },
                        ticks: {
                          color: '#4A5568' // gray.600
                        },
                        grid: {
                          color: '#E2E8F0' // gray.200
                        }
                      },
                      y: {
                        display: false, // Hide the y-axis completely
                        grid: {
                          display: false // Hide the y-axis grid lines
                        }
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Categories summary box */}
          <Box p={5} shadow="md" borderWidth="1px" borderRadius="md">
            <Heading size="md" mb={4}>Categories</Heading>
            {getCategorySummary().map(({ category, count }) => (
              <Box 
                key={category} 
                p={3} 
                mb={2} 
                borderWidth="1px" 
                borderRadius="md"
                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                onClick={() => navigate(`/category/${encodeURIComponent(category)}?race_code=${race_code}`)}
              >
                <Text fontSize="lg">
                  <Text as="span" fontWeight="bold">{category}</Text>
                  <Text as="span" color="gray.500" ml={2}>({count} racers)</Text>
                </Text>
              </Box>
            ))}
          </Box>
        </SimpleGrid>

        {/* Results section stays the same */}
        <Heading size="md" mb={4}>Results</Heading>
        {Object.entries(racersByCategory).map(([category, racers]) => (
          <Box key={category} mb={8}>
            <Heading size="sm" mb={4} color="gray.600">{category}</Heading>
            <Box overflowX="auto">
              <ResultsTable racers={racers} />
            </Box>
          </Box>
        ))}
      </Box>
    </Container>
  );
} 